import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faDesktop } from "@fortawesome/free-solid-svg-icons";
import UserSettingsAPI from "../../services/UserSettingsAPI";
import InstallAppPanel from "../../InstallAppPanel";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/PreferenceSettings.css";

const ADA_WIDGET_ENABLED_KEY = "ada-widget-enabled";

const AppearanceSettings = ({
  theme,
  setTheme,
  effectiveTheme,
  handleThemeChange,
  getThemeIcon,
  getThemeLabel,
}) => {
  const { t } = useTranslation();
  // Landing page preference state
  const [landingPage, setLandingPage] = useState("Dashboard");

  // Accessibility widget toggle state
  const [accessibilityEnabled, setAccessibilityEnabled] = useState(
    () => localStorage.getItem(ADA_WIDGET_ENABLED_KEY) !== "false",
  );

  // Sync accessibility toggle when dismissed from the widget itself
  useEffect(() => {
    const handler = (e) => setAccessibilityEnabled(e.detail.enabled);
    window.addEventListener("adaWidgetEnabledChanged", handler);
    return () => window.removeEventListener("adaWidgetEnabledChanged", handler);
  }, []);

  const handleAccessibilityToggle = () => {
    const newVal = !accessibilityEnabled;
    setAccessibilityEnabled(newVal);
    localStorage.setItem(ADA_WIDGET_ENABLED_KEY, String(newVal));
    window.dispatchEvent(
      new CustomEvent("adaWidgetEnabledChanged", {
        detail: { enabled: newVal },
      }),
    );
  };

  // Load preferences from localStorage on component mount, then refresh from API
  useEffect(() => {
    const savedLandingPage = localStorage.getItem("landingPage") || "Dashboard";
    setLandingPage(savedLandingPage);

    // Refresh from backend (source of truth) and update local state
    UserSettingsAPI.getSettings().then((settings) => {
      if (settings) {
        setLandingPage(settings.defaultLandingPage || "Dashboard");
      }
    });
  }, []);

  return (
    <>
      <div className="common-form-group">
        <h4>{t("appearance.landingPage.title")}</h4>
        <div className="common-form-row indented-below-h4">
          <div className="common-form-field">
            <label htmlFor="landing-page-select">
              {t("appearance.landingPage.label")}
            </label>
            <select
              id="landing-page-select"
              value={landingPage}
              onChange={(e) => {
                const newPage = e.target.value;
                setLandingPage(newPage);
                localStorage.setItem("landingPage", newPage);
                UserSettingsAPI.updateSettings({
                  defaultLandingPage: newPage,
                }).catch(() => {});
              }}
            >
              <option value="Dashboard">
                {t("appearance.landingPage.dashboard")}
              </option>
              <option value="Getting Started">
                {t("appearance.landingPage.gettingStarted")}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div className="common-form-group">
        <h4>{t("appearance.theme.title")}</h4>
        <div className="common-form-row indented-below-h4">
          <div className="common-form-field">
            <label htmlFor="theme-select">
              {t("appearance.theme.colorLabel")}
            </label>
            <select
              id="theme-select"
              value={theme}
              onChange={handleThemeChange}
            >
              <option value="light">
                <FontAwesomeIcon icon={faSun} />{" "}
                {t("appearance.theme.optLight")}
              </option>
              <option value="dark">
                <FontAwesomeIcon icon={faMoon} />{" "}
                {t("appearance.theme.optDark")}
              </option>
              <option value="auto">
                <FontAwesomeIcon icon={faDesktop} />{" "}
                {t("appearance.theme.optAuto")}
              </option>
            </select>
          </div>
        </div>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("appearance.compactMode.title")}</h4>
            <p>{t("appearance.compactMode.desc")}</p>
          </div>
          <div className="common-toggle"></div>
        </div>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("appearance.accessibility.title")}</h4>
            <p>{t("appearance.accessibility.desc")}</p>
          </div>
          <div
            className={`common-toggle${accessibilityEnabled ? " active" : ""}`}
            onClick={handleAccessibilityToggle}
            role="switch"
            aria-checked={accessibilityEnabled}
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              handleAccessibilityToggle()
            }
          />
        </div>
      </div>

      <div className="common-form-group">
        <h4>{t("appearance.installApp.title")}</h4>
        <div className="common-form-row indented-below-h4">
          <div className="common-form-field">
            <InstallAppPanel />
          </div>
        </div>
      </div>
    </>
  );
};

export default AppearanceSettings;
